import { Navigate, Outlet, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { invalidateClientSession } from "@/shared/utils/authSession.util";
import { useAuth } from "@/hooks/useAuth";
import { isClusterPortalRole } from "@/shared/utils/clusterPortalRole.util";
import { RouteFallback } from "@/routes/RouteFallback";
import { isExpoVisitorRole } from "@/shared/utils/expoVisitorRole.util";

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
};

export function FacilitatorProtectedRoute() {
  const location = useLocation();
  const { token, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <RouteFallback />;
  }

  if (!isAuthenticated || !token) {
    return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);

    if (decoded.role === "ADMIN" || decoded.role === "SUB_ADMIN") {
      return <Navigate to="/admin/companies" replace />;
    }

    if (decoded.role === "MANAGER") {
      return <Navigate to="/management" replace />;
    }

    if (decoded.role === "CLIENT" || isExpoVisitorRole(decoded.role)) {
      return <Navigate to={isExpoVisitorRole(decoded.role) ? "/my-discovery" : "/home"} replace />;
    }

    if (decoded.role === "EXPERT") {
      return <Navigate to="/expert/dashboard" replace />;
    }

    if (isClusterPortalRole(decoded.role)) {
      return <Navigate to="/ecosystem-head/dashboard" replace />;
    }

    if (decoded.role === "EXPO_HEAD") {
      return <Navigate to="/expo-head/dashboard" replace />;
    }

    if (decoded.role !== "FACILITATOR") {
      invalidateClientSession();
      return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
    }

    return <Outlet />;
  } catch {
    invalidateClientSession();
    return <Navigate to="/facilitator/login" state={{ from: location }} replace />;
  }
}
